// src/lib/chatAI.ts
// Chat may run on a different Supabase instance from the site's contact/auth data.
const CHAT_SUPABASE_URL = import.meta.env.VITE_CHAT_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL;
const CHAT_SUPABASE_ANON_KEY = import.meta.env.VITE_CHAT_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;
const EDGE_URL = `${CHAT_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/chat-ai`;

export type ChatMsg = { role: 'user' | 'assistant' | 'system'; content: string };


export async function askAI(msgs, opts) {
    const res = await fetch(EDGE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: CHAT_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${CHAT_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        model: opts?.model ?? 'gpt-4o-mini',
        system: opts?.system ?? "", // ← use the strong system prompt
        messages: msgs,
        stream: false,
      }),
    });
    if (!res.ok) throw new Error(`Edge function failed: ${res.status} ${await res.text()}`);
    const data = await res.json();
    return data.text ?? '';
  }

/** Optional: streaming variant (SSE) */
export async function streamAI(
  msgs: ChatMsg[],
  onToken: (delta: string) => void,
  opts?: { system?: string; model?: string }
): Promise<void> {
  const res = await fetch(EDGE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      apikey: CHAT_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${CHAT_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      model: opts?.model ?? 'gpt-4o',
      system: opts?.system ?? "", // ← use the strong system prompt
      messages: msgs,
      stream: true,
    }),
  });
  if (!res.ok || !res.body) throw new Error('Stream failed');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value);
    for (const line of chunk.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;
      const payload = trimmed.slice(5).trim();
      if (payload === '[DONE]') return;
      try {
        onToken(JSON.parse(payload)); // we emitted JSON-encoded deltas
      } catch {
        onToken(payload);
      }
    }
  }
}
